import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import TopPanel from "./TableComponents/TopPanel";
import MainTable from "./TableComponents/MainTable";
import VehicleDivisionFailureModal from "./TableComponents/Modals/VehicleDivisionFailureModal";
import VehiclesLoadingModal from "./TableComponents/Modals/VehiclesLoadingModal";
import { useFetchBusDivisions } from "../../hooks/use-fetch-bus-divisions";
import { useFetchCodes } from "../../hooks/use-fetch-codes";
import { useFetchService } from "../../hooks/use-fetch-service";
import { getUserDivision } from "../../utils/TableHelpers/getUserDivision";
import { handleDataWarnings } from "./TableHelperFunctions/handleDataWarnings";
import { addFailedUpdateDataToTableData } from "./TableHelperFunctions/addFailedUpdateDataToTableData";
import { resolveBoxJump } from "../../utils/TableHelpers/resolveBoxJump";
import { AuthContext } from "../../utils/AuthContext";

const Table = ({
  TableContext,
  fetchBusBlocks,
  // fetchBusBlocksByNumber,
  setApiErrors,
  isSpecial = false,
}) => {
  const [assignmentDate, setAssignmentDate] = useState(new Date());
  const [serviceType, setServiceType] = useState("");
  const [rowFilter, setRowFilter] = useState("ALL");
  const [pulloutDivision, setPulloutDivision] = useState("0");
  const [vehicleDivision, setVehicleDivision] = useState("0");
  const [selected, setSelected] = useState(-1);
  const [boxJumpTo, setBoxJumpTo] = useState(null);
  const [pulloutDivisions, setPulloutDivisions] = useState([]);
  const [rowFilterCodes, setRowFilterCodes] = useState([]);
  const [duplicateBuses, setDuplicateBuses] = useState([]);
  const [exceptionFilters, setExceptionFilters] = useState([]);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [blockMessages, setBlockMessages] = useState([]);
  const [updateErrors, setUpdateErrors] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [updatedData, setUpdatedData] = useState([]);
  // Scoped tighter than `errorMessage` so the disable gate ignores
  // unrelated errors (box-jump, save, service).
  const [tableFetchFailed, setTableFetchFailed] = useState(false);
  const [vehicleFailureDivisionId, setVehicleFailureDivisionId] =
    useState(null);
  const previousVehicleDivisionRef = useRef("0");
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fetchBusDivisions = useFetchBusDivisions();
  const fetchCodes = useFetchCodes();
  const fetchService = useFetchService();
  const { userDivisions, setCurrentDivision } = useContext(AuthContext);

  setCurrentDivision(pulloutDivision);

  const getDivisions = useCallback(async () => {
    const response = await fetchBusDivisions();

    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
        setLoadingData(false);
      }
      return;
    }

    const divisionResult = getUserDivision(response.data, userDivisions);

    if (!divisionResult) {
      return null;
    }

    const { userDivisionList, defaultDivisionId } = divisionResult;

    setPulloutDivisions(userDivisionList);
    setPulloutDivision(defaultDivisionId);
    setVehicleDivision(defaultDivisionId);
  }, [fetchBusDivisions, setApiErrors, userDivisions]);

  const getRowFilters = useCallback(async () => {
    const response = await fetchCodes();
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
      }
      return;
    }
    const responseData = response.data;
    setRowFilterCodes(responseData);
  }, [fetchCodes, setApiErrors]);

  useEffect(() => {
    const fetchServiceData = async () => {
      const response = await fetchService();
      if (!response.success) {
        if (response.code === 501) {
          setApiErrors([response]);
        } else {
          setErrorMessage(response.message);
        }
        return;
      }

      const responseData = response.data;
      setServiceType(responseData);
      getDivisions();
      getRowFilters();
    };

    // Floating this promise swallowed any throw from the setters below it.
    fetchServiceData().catch((error) => {
      setErrorMessage(error?.message || "An error has occurred while fetching service data");
    });
  }, [fetchService, getDivisions, getRowFilters, setApiErrors]);

  const handleDateChange = async (date) => {
    const response = await fetchService(date);
    if (!response.success) {
      if (response.code === 501) {
        setApiErrors([response]);
      } else {
        setErrorMessage(response.message);
      }
      return;
    }
    setAssignmentDate(date);
    const responseData = response.data;
    setServiceType(responseData);
  };

  const fetchData = useCallback(
    async (failedUpdateData) => {
      setLoadingData(true);
      setSuccessMessage("");
      setErrorMessage("");
      setBlockMessages([]);
      setUpdateErrors([]);

      const response = await fetchBusBlocks(
        assignmentDate,
        rowFilter,
        pulloutDivision
      );

      if (!response.success) {
        if (response.code === 501) {
          setApiErrors([response]);
        } else {
          setErrorMessage(response.message);
          setTableData([]);
          setUpdatedData([]);
          setSelectedVehicles([]);
          setDuplicateBuses([]);
          setTableFetchFailed(true);
          setLoadingData(false);
        }
        return;
      }

      setTableFetchFailed(false);
      const responseData = response.data;
      // Exception filtering now happens in UI via filteredTableData memo below
      // const dataWithWarnings = handleDataWarnings(responseData).filter(
      //   (row) => {
      //     if (exceptionFilters.length === 0) {
      //       return true;
      //     }
      //     if (
      //       exceptionFilters.includes("vehicleTypeMismatch") &&
      //       row.vehicleTypeMismatch
      //     ) {
      //       return true;
      //     }
      //     return exceptionFilters.includes(row.pulloutVarianceReason);
      //   }
      // );
      const dataWithWarnings = handleDataWarnings(responseData);
      const dataWithUpdates = addFailedUpdateDataToTableData(
        dataWithWarnings,
        failedUpdateData
      );
      setSelected(-1);
      setBoxJumpTo(null);
      if (failedUpdateData) {
        setUpdatedData(dataWithUpdates);
      } else {
        setUpdatedData([]);
      }
      setTableData(dataWithWarnings);
      const returnedSelectedVehicles = dataWithUpdates
        .filter((row) => row.lastVehicle)
        .map((row, i) => `${i}-${row.lastVehicle}`);
      setSelectedVehicles(returnedSelectedVehicles);
      const returnedDuplicateVehicles = dataWithUpdates.filter(
        (row) => row.duplicateBus
      );
      setDuplicateBuses(returnedDuplicateVehicles);
      setLastUpdated(new Date());
      setLoadingData(false);
    },
    [
      assignmentDate,
      fetchBusBlocks,
      pulloutDivision,
      rowFilter,
      setApiErrors,
    ]
  );

  useEffect(() => {
    // "0" is the pre-selection placeholder. Skipping avoids a 403 from the
    // backend, which has no division 0 in any user's permission set.
    if (pulloutDivision === "0") return;
    fetchData();
  }, [assignmentDate, rowFilter, pulloutDivision, fetchData]);

  // Clears prior-division state in the same commit as the dropdown change so
  // no stale rows render under the new selection before fetchData fires.
  const handlePulloutDivisionChange = useCallback((nextDivision) => {
    setPulloutDivision(nextDivision);
    setTableData([]);
    setUpdatedData([]);
    setSelectedVehicles([]);
    setDuplicateBuses([]);
    setBoxJumpTo(null);
    setErrorMessage("");
    setTableFetchFailed(false);
  }, []);

  // Captures the prior vehicleDivision so handleVehicleFetchFailed can revert.
  const handleVehicleDivisionChange = useCallback(
    (nextDivision) => {
      previousVehicleDivisionRef.current = vehicleDivision;
      setVehicleDivision(nextDivision);
    },
    [vehicleDivision]
  );

  // Two skip guards: stale-closure (user already moved on) and lockstep
  // (matching pulloutDivision means the pullout failure UX already covers it).
  const handleVehicleFetchFailed = useCallback(
    (failedDivisionId) => {
      if (failedDivisionId?.toString() !== vehicleDivision?.toString()) {
        return;
      }
      if (failedDivisionId?.toString() === pulloutDivision?.toString()) {
        return;
      }
      setVehicleFailureDivisionId(failedDivisionId);
      setVehicleDivision(previousVehicleDivisionRef.current);
      setErrorMessage("");
    },
    [vehicleDivision, pulloutDivision]
  );

  const dismissVehicleFailureModal = useCallback(() => {
    setVehicleFailureDivisionId(null);
  }, []);

  const failedVehicleDivisionName = useMemo(() => {
    if (vehicleFailureDivisionId == null) return "";
    const match = pulloutDivisions.find(
      (d) =>
        d.divisionId?.toString() === vehicleFailureDivisionId?.toString()
    );
    return match?.divisionName ?? "";
  }, [pulloutDivisions, vehicleFailureDivisionId]);

  const filteredTableData = useMemo(() => {
    if (exceptionFilters.length === 0) {
      return tableData;
    }
    return tableData.filter((row) => {
      if (
        exceptionFilters.includes("vehicleTypeMismatch") &&
        row.vehicleTypeMismatch
      ) {
        return true;
      }
      return exceptionFilters.includes(row.pulloutVarianceReason);
    });
  }, [tableData, exceptionFilters]);

  const jumpToBoxInUI = useCallback(() => {
    const rowIndex = resolveBoxJump({
      boxJumpTo,
      rows: filteredTableData,
      loadingData,
    });

    // null → data not ready (loading/empty) or no active jump; don't flag.
    if (rowIndex === null) return;

    if (rowIndex !== -1) {
      setSelected(rowIndex);
    } else {
      setErrorMessage(`Box ${boxJumpTo} not found in current view. Try clearing filters.`);
    }
  }, [boxJumpTo, filteredTableData, loadingData]);

  useEffect(() => {
    if (boxJumpTo) {
      jumpToBoxInUI();
    }
  }, [boxJumpTo, jumpToBoxInUI]);

  const clearAllFilters = useCallback(() => {
    setRowFilter("ALL");
    setExceptionFilters([]);
  }, []);

  // Decide what to render below TopPanel. Three mutually exclusive states:
  //   1. The user is authenticated and has divisions in their permissions
  //      cookie, but none appeared in /busDivisions — show an explicit
  //      empty-state message. (Previously this path crashed MainTable on
  //      `pulloutDivision.toString()` because pulloutDivision was null.)
  //   2. We have a valid pullout/vehicle division — render MainTable.
  //   3. Otherwise we're still loading the initial division data.
  // Flat if/else (vs. a nested ternary in JSX) per SonarCloud S3358.
  const hasNoAvailableDivisions =
    pulloutDivision === null && userDivisions?.length > 0;
  const isReadyForMainTable =
    pulloutDivision != null &&
    pulloutDivision !== "0" &&
    vehicleDivision != null &&
    vehicleDivision !== "0" &&
    userDivisions;

  let tableBody;
  if (hasNoAvailableDivisions) {
    tableBody = (
      <div
        style={{ textAlign: "center", marginTop: "2rem", padding: "0 1rem" }}
      >
        <h4>No available divisions for your account.</h4>
        <p>
          Your account is configured with division access, but none of your
          assigned divisions are currently available for assignment
          scheduling. Contact your administrator if this is unexpected.
        </p>
      </div>
    );
  } else if (isReadyForMainTable) {
    tableBody = (
      <MainTable
        assignmentDate={assignmentDate}
        blockMessages={blockMessages}
        boxJumpTo={boxJumpTo}
        clearAllFilters={clearAllFilters}
        data={filteredTableData}
        errorMessage={errorMessage}
        exceptionFilters={exceptionFilters}
        getData={fetchData}
        isSpecial={isSpecial}
        lastUpdated={lastUpdated}
        loadingTableData={loadingData}
        onVehicleFetchFailed={handleVehicleFetchFailed}
        pulloutDivision={pulloutDivision}
        rowFilter={rowFilter}
        selected={selected}
        selectedVehicles={selectedVehicles}
        setApiErrors={setApiErrors}
        setBlockMessages={setBlockMessages}
        setBoxJumpTo={setBoxJumpTo}
        setErrorMessage={setErrorMessage}
        setExceptionFilters={setExceptionFilters}
        setSelectedVehicles={setSelectedVehicles}
        setSuccessMessage={setSuccessMessage}
        setUpdateErrors={setUpdateErrors}
        setUpdatedData={setUpdatedData}
        setVehiclesLoading={setVehiclesLoading}
        successMessage={successMessage}
        tableFetchFailed={tableFetchFailed}
        TableContext={TableContext}
        updatedData={updatedData}
        updateErrors={updateErrors}
        userDivisions={userDivisions}
        vehicleDivision={vehicleDivision}
      />
    );
  } else {
    tableBody = (
      <div style={{ textAlign: "center", marginTop: "1rem" }}>
        <h4 className="loading">Loading ........</h4>
      </div>
    );
  }

  return (
    <div style={{
      width: "100%",
      padding: "0 10px",
      position: "relative",
      zIndex: 0,
      display: "flex",
      flexDirection: "column",
      height: "calc(100vh - 30px - var(--banner-height, 0px))",
    }}>
      <TopPanel
        assignmentDate={assignmentDate}
        duplicateBuses={duplicateBuses}
        handleDateChange={handleDateChange}
        pulloutDivision={pulloutDivision}
        pulloutDivisions={pulloutDivisions}
        rowFilter={rowFilter}
        rowFilterCodes={rowFilterCodes}
        serviceType={serviceType}
        setBoxJumpTo={setBoxJumpTo}
        setDuplicateBuses={setDuplicateBuses}
        setPulloutDivision={handlePulloutDivisionChange}
        setRowFilter={setRowFilter}
        setVehicleDivision={handleVehicleDivisionChange}
        tableFetchFailed={tableFetchFailed}
        updatedData={updatedData}
        vehicleDivision={vehicleDivision}
      />
      {tableBody}
      <VehicleDivisionFailureModal
        isOpen={vehicleFailureDivisionId !== null}
        onDismiss={dismissVehicleFailureModal}
        failedDivisionName={failedVehicleDivisionName}
      />
      <VehiclesLoadingModal
        isOpen={
          vehiclesLoading &&
          !loadingData &&
          vehicleFailureDivisionId === null
        }
      />
    </div>
  );
};

Table.propTypes = {
  TableContext: PropTypes.object.isRequired,
  fetchBusBlocks: PropTypes.func.isRequired,
  fetchBusBlocksByNumber: PropTypes.func.isRequired,
  setApiErrors: PropTypes.func,
  isSpecial: PropTypes.bool,
};

export default Table;
